import ReactDOM from 'react-dom/client'
import { KindeProvider } from '@kinde-oss/kinde-auth-react'
import { Capacitor } from '@capacitor/core'
import App from './App.tsx'
import './index.css'

import { debugLog } from '@/lib/debugLog'
import { configureNativeViewport } from '@/lib/configureNativeViewport'

// CRITICAL FIX: Clean HTML-encoded URLs before Kinde SDK processes them
// This must happen BEFORE any React rendering or Kinde initialization
// Note: We replace the URL without reloading to preserve the OAuth code (single-use)
if (window.location.href.includes('&amp;')) {
  console.warn('[App] Detected HTML entities in URL, cleaning before Kinde processes it...');
  const cleanUrl = window.location.href.replace(/&amp;/g, '&');
  
  // Security: Validate that cleanUrl is still on the same origin to prevent open redirect
  try {
    const currentOrigin = window.location.origin;
    const cleanUrlObj = new URL(cleanUrl);
    if (cleanUrlObj.origin !== currentOrigin) {
      console.error('[App] Cleaned URL has different origin, potential security issue');
      window.location.href = '/login?error=invalid_redirect';
      throw new Error('Invalid redirect URL');
    }
    
    // Replace URL without reloading to preserve OAuth code
    // The Kinde SDK reads from window.location, so updating it should work
    window.history.replaceState(null, '', cleanUrl);
    console.log('[App] URL cleaned successfully, proceeding with Kinde initialization');
  } catch (e) {
    // If URL parsing fails, redirect to login as fallback
    console.error('[App] Failed to parse cleaned URL:', e);
    window.location.href = '/login?error=invalid_url';
    throw new Error('Invalid URL format');
  }
}

// Detect if running in native Capacitor container
const isNativePlatform = Capacitor.isNativePlatform();

configureNativeViewport(document);

// Ensure the Vite PWA service worker is registered (needed for push notifications).
// Skip PWA registration on native platforms - they have their own push notification system.
// In development, this enables the dev service worker when `vite-plugin-pwa` devOptions.enabled is true.
import { registerSW } from 'virtual:pwa-register'

// If the app is served with a mismatched set of chunks (common after a deploy with aggressive caching
// or a stale service worker), Vite can emit a preload error.
// On web we recover by unregistering SWs and reloading once.
// On native (Capacitor), forced reloads can cause unstable startup loops, so we only log.
// See: https://vitejs.dev/guide/build.html#chunking-strategy
if (isNativePlatform) {
  window.addEventListener('vite:preloadError', (event: Event) => {
    event.preventDefault?.()
    console.error('[App] Vite preload error on native platform. Suppressing forced reload to avoid startup loops.');
  })
} else {
  window.addEventListener('vite:preloadError', (event: Event) => {
    // Prevent Vite from throwing the error again.
    event.preventDefault?.()

    const key = 'cleariest:preloadErrorReloaded'
    if (sessionStorage.getItem(key) === '1') {
      return
    }
    sessionStorage.setItem(key, '1')

    const reload = () => window.location.reload()

    if (!('serviceWorker' in navigator)) {
      reload()
      return
    }

    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => Promise.all(registrations.map((r) => r.unregister())))
      .catch(() => {
        // Best-effort; still reload.
      })
      .finally(reload)
  })
}

// Only register PWA service worker on web platform
if (!isNativePlatform) {
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      debugLog('[PWA] New service worker available, updating...')
      // Activate updated SW and reload so the new bundle is used.
      updateSW(true)
    },
    onOfflineReady() {
      debugLog('[PWA] App ready to work offline')
    },
    onRegistered(registration) {
      debugLog('[PWA] Service worker registered')
      // Force update check
      if (registration) {
        registration.update();
      }
    },
  });
}

// Kinde configuration
const runtimeEnv = window.__ENV

const appOrigin = window.location.origin
const defaultRedirectUri = `${appOrigin}/auth/callback`

/**
 * Get platform-specific redirect URI for OAuth
 * Native apps use custom URL scheme, web uses https
 */
const getRedirectUri = (): string => {
  if (isNativePlatform) {
    return 'cleariest://auth/callback';
  }
  return normalizeRedirectUri(
    runtimeEnv?.VITE_KINDE_REDIRECT_URI ?? import.meta.env.VITE_KINDE_REDIRECT_URI
  );
};

/**
 * Get platform-specific logout URI
 */
const getLogoutUri = (): string => {
  if (isNativePlatform) {
    return 'cleariest://auth/logout';
  }
  return runtimeEnv?.VITE_KINDE_LOGOUT_URI ?? import.meta.env.VITE_KINDE_LOGOUT_URI ?? appOrigin;
};

const normalizeRedirectUri = (value: string | undefined | null): string => {
  if (!value) return defaultRedirectUri

  // Common misconfig: redirect set to the bare origin. Our app expects /auth/callback.
  if (value === appOrigin || value === `${appOrigin}/`) {
    return defaultRedirectUri
  }

  return value
}

const kindeConfig = {
  clientId: runtimeEnv?.VITE_KINDE_CLIENT_ID ?? import.meta.env.VITE_KINDE_CLIENT_ID ?? '',
  domain: runtimeEnv?.VITE_KINDE_DOMAIN ?? import.meta.env.VITE_KINDE_DOMAIN ?? '',
  ...(runtimeEnv?.VITE_KINDE_AUDIENCE || import.meta.env.VITE_KINDE_AUDIENCE || runtimeEnv?.VITE_API_URL || import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL
    ? {
        audience:
          runtimeEnv?.VITE_KINDE_AUDIENCE ??
          import.meta.env.VITE_KINDE_AUDIENCE ??
          runtimeEnv?.VITE_API_URL ??
          import.meta.env.VITE_API_URL ??
          import.meta.env.VITE_API_BASE_URL,
      }
    : {}),
  redirectUri: getRedirectUri(),
  logoutUri: getLogoutUri(),
  refreshOnFocus: !isNativePlatform,
  callbacks: {
    onError: (error: any) => {
      console.error('[Kinde] Authentication error:', error);
      // Store minimal error info for debugging (not sensitive details)
      sessionStorage.setItem('kinde_last_error', JSON.stringify({
        time: new Date().toISOString(),
        message: 'Authentication failed'
      }));
      // Redirect to login with error - use relative path to prevent open redirect
      const errorMessage = encodeURIComponent(error.message || 'Authentication failed');
      window.location.href = `/login?error=kinde_error&msg=${errorMessage}`;
    },
    onSuccess: (user: any) => {
      console.log('[Kinde] Authentication successful for:', user.email);
      // Clear any previous errors
      sessionStorage.removeItem('kinde_last_error');
    }
  }
}

// Note: StrictMode is disabled to prevent double-mounting which causes
// SignalR connection issues in development. Re-enable for production.
ReactDOM.createRoot(document.getElementById('root')!).render(
  <KindeProvider {...kindeConfig}>
    <App />
  </KindeProvider>
)
